import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useLocation, useNavigate } from 'react-router-dom';

const AddCommunityForm = () => {
  const { id } = useParams(); // Get ID from URL if editing
  const location = useLocation();
  const navigate = useNavigate();
  const isEditing = !!id;

  const initialFormState = {
    title: '',
    subheading: '',
    description: '',
    averageValue: '',
    pricePerSqFt: '',
    homesForSale: '',
    images: [''],
    schools: [],
    parks: [],
    transit: []
  };
  
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(initialFormState);

  const backendUrl = 'https://python-backend-68898252993.us-central1.run.app';
// const backendUrl = 'http://127.0.0.1:5001';


useEffect(() => {
  if (isEditing) {
    // First try to use data passed via location state 
    if (location.state?.community) {
      prepareFormData(location.state.community);
    } else {
      // If not available, fetch from API
      fetchCommunityData();
    }
  }
}, [id, location]);



  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const fetchCommunityData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${backendUrl}/api/communities/${id}`);
      prepareFormData(response.data);
    } catch (error) {
      console.error('Error fetching community data:', error);
      setError('Failed to load community data. Please try again.');
    } finally {
      setLoading(false);
    }
  };


    // Prepare and format data for the form
    const prepareFormData = (community) => {
      setFormData({
        title: community.title || '',
        subheading: community.subheading || '',
        description: community.description || '',
        averageValue: community.averageValue || '',
        pricePerSqFt: community.pricePerSqFt || '',
        homesForSale: community.homesForSale?.toString() || '',
        images: community.images?.length ? community.images : [''],
        schools: community.schools?.length ? community.schools : [],
        parks: community.parks?.length ? community.parks : [],
        transit: community.transit?.length ? community.transit : []
      });
    };


  const handleImageChange = (index, value) => {
    const newImages = [...formData.images];
    newImages[index] = value;
    setFormData({
      ...formData,
      images: newImages
    });
  };

  const addImageField = () => {
    setFormData({
      ...formData,
      images: [...formData.images, '']
    });
  };

  const removeImageField = (index) => {
    const newImages = [...formData.images];
    newImages.splice(index, 1);
    setFormData({
      ...formData,
      images: newImages
    });
  };

  // School methods
  const addSchool = () => {
    setFormData({
      ...formData,
      schools: [...formData.schools, { name: '', grades: '', type: 'Public', rating: '', image: '' }]
    });
  };

  const handleSchoolChange = (index, field, value) => {
    const newSchools = [...formData.schools];
    newSchools[index] = {
      ...newSchools[index],
      [field]: value
    };
    setFormData({
      ...formData,
      schools: newSchools
    });
  };

  const removeSchool = (index) => {
    const newSchools = [...formData.schools];
    newSchools.splice(index, 1);
    setFormData({
      ...formData,
      schools: newSchools
    });
  };

  // Park methods
  const addPark = () => {
    setFormData({
      ...formData,
      parks: [...formData.parks, { name: '', amenities: '', image: '' }]
    });
  };

  const handleParkChange = (index, field, value) => {
    const newParks = [...formData.parks];
    newParks[index] = {
      ...newParks[index],
      [field]: value
    };
    setFormData({
      ...formData,
      parks: newParks
    });
  };

  const removePark = (index) => {
    const newParks = [...formData.parks];
    newParks.splice(index, 1);
    setFormData({
      ...formData,
      parks: newParks
    });
  };

  // Transit methods
  const addTransit = () => {
    setFormData({
      ...formData,
      transit: [...formData.transit, { mode: '', name: '', description: '' }]
    });
  };

  const handleTransitChange = (index, field, value) => {
    const newTransit = [...formData.transit];
    newTransit[index] = {
      ...newTransit[index],
      [field]: value
    };
    setFormData({
      ...formData,
      transit: newTransit
    });
  };

  const removeTransit = (index) => {
    const newTransit = [...formData.transit];
    newTransit.splice(index, 1);
    setFormData({
      ...formData,
      transit: newTransit
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      // Validate required fields
      if (!formData.title || !formData.description || !formData.averageValue || !formData.homesForSale) {
        throw new Error('Please fill in all required fields');
      }

      const token = localStorage.getItem('token');
      let response;
      
      if (isEditing) {
        // Update existing community
        response = await axios.put(
          `${backendUrl}/api/communities/${id}`, 
          formData,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
      } else {
        // Create new community
        response = await axios.post(
          `${backendUrl}/api/communities`, 
          formData
        );
      }
      
      if (response.data.success) {
        setSuccess(true);
        
        // After success
        setTimeout(() => {
          if (isEditing) {
            // If editing, go back to the community detail page
            navigate(`/community/${id}`);
          } else {
            // If creating new, reset form
            setFormData(initialFormState);
          }
        }, 2000);
      } else {
        throw new Error(isEditing ? 'Failed to update community' : 'Failed to create community');
      }
    } catch (err) {
      setError(err.message || `An error occurred while ${isEditing ? 'updating' : 'creating'} the community`);
    } finally {
      setLoading(false);
    }
  };



  return (
    <div className="max-w-4xl mx-auto mt-12 mb-20 p-6 bg-white rounded-xl shadow-lg">
      
      <h1 className="text-3xl font-bold text-center mb-8">
        {isEditing ? 'Edit Community' : 'Add a New Community'}
      </h1>      

        {success && (
        <div className="mb-6 p-4 bg-green-100 text-green-700 rounded-md">
          Community {isEditing ? 'updated' : 'created'} successfully!
          <button 
            className="ml-4 underline"
            onClick={() => isEditing ? navigate(`/community/${id}`) : navigate('/')}
          >
            {isEditing ? 'Return to community' : 'Return to communities'}
          </button>
        </div>
      )}
      

      {error && (
        <div className="mb-6 p-4 bg-red-100 text-red-700 rounded-md">
          {error}
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Info Section */}
        <div className="border border-gray-200 rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-4">Basic Information</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                Subheading
              </label>
              <input
                type="text"
                name="subheading"
                value={formData.subheading}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div className="md:col-span-2">
              <label className="block text-gray-700 font-medium mb-2">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="4"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              ></textarea>
            </div>
            
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                Average Home Value <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="averageValue"
                value={formData.averageValue}
                onChange={handleInputChange}
                placeholder="e.g. $500K or $1.2M"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                Price Per Square Foot
              </label>
              <input
                type="text"
                name="pricePerSqFt"
                value={formData.pricePerSqFt}
                onChange={handleInputChange}
                placeholder="e.g. 250"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                Homes For Sale <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="homesForSale"
                value={formData.homesForSale}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>
        </div>
        
        {/* Images Section */}
        <div className="border border-gray-200 rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-4">Images</h2>
          
          {formData.images.map((image, index) => (
            <div key={index} className="flex items-center mb-4">
              <input
                type="url"
                value={image}
                onChange={(e) => handleImageChange(index, e.target.value)}
                placeholder="Enter image URL"
                className="flex-grow px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {index > 0 && (
                <button
                  type="button"
                  onClick={() => removeImageField(index)}
                  className="ml-2 p-2 text-red-500 hover:text-red-700"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 000 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                  </svg>
                </button>
              )}
            </div>
          ))}
          
          <button
            type="button"
            onClick={addImageField}
            className="px-4 py-2 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 transition-colors flex items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
            </svg>
            Add Another Image
          </button>
        </div>
        
        {/* Schools Section */}
        <div className="border border-gray-200 rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-4">Schools</h2>
          
          {formData.schools.length === 0 ? (
            <p className="text-gray-500 mb-4">No schools added yet.</p>
          ) : (
            formData.schools.map((school, index) => (
              <div key={index} className="p-4 mb-4 border border-gray-200 rounded-lg">
                <div className="flex justify-between mb-2">
                  <h3 className="font-medium">School #{index + 1}</h3>
                  <button
                    type="button"
                    onClick={() => removeSchool(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    Remove
                  </button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-1">
                      Name
                    </label>
                    <input
                      type="text"
                      value={school.name}
                      onChange={(e) => handleSchoolChange(index, 'name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-1">
                      Grades
                    </label>
                    <input
                      type="text"
                      value={school.grades}
                      onChange={(e) => handleSchoolChange(index, 'grades', e.target.value)}
                      placeholder="e.g. K-5, 6-8, 9-12"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-1">
                      Type
                    </label>
                    <select
                      value={school.type}
                      onChange={(e) => handleSchoolChange(index, 'type', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="Public">Public</option>
                      <option value="Private">Private</option>
                      <option value="Charter">Charter</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-1">
                      Rating
                    </label>
                    <input
                      type="text"
                      value={school.rating}
                      onChange={(e) => handleSchoolChange(index, 'rating', e.target.value)}
                      placeholder="e.g. A, B+, C"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-gray-700 text-sm font-medium mb-1">
                      Image URL
                    </label>
                    <input
                      type="url"
                      value={school.image}
                      onChange={(e) => handleSchoolChange(index, 'image', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>
            ))
          )}
          
          <button
            type="button"
            onClick={addSchool}
            className="px-4 py-2 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 transition-colors flex items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
            </svg>
            Add School
          </button>
        </div>
        
        {/* Parks Section */}
        <div className="border border-gray-200 rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-4">Parks</h2>
          
          {formData.parks.length === 0 ? (
  <p className="text-gray-500 mb-4">No parks added yet.</p>
) : (
  formData.parks.map((park, index) => (
    <div key={index} className="p-4 mb-4 border border-gray-200 rounded-lg">
      <div className="flex justify-between mb-2">
        <h3 className="font-medium">Park #{index + 1}</h3>
        <button
          type="button"
          onClick={() => removePark(index)}
          className="text-red-500 hover:text-red-700"
        >
          Remove
        </button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
        <div className="md:col-span-2">
          <label className="block text-gray-700 text-sm font-medium mb-1">
            Name
          </label>
          <input
            type="text"
            value={park.name}
            onChange={(e) => handleParkChange(index, 'name', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        
        <div className="md:col-span-2">
          <label className="block text-gray-700 text-sm font-medium mb-1">
            Amenities
          </label>
          <input
            type="text"
            value={park.amenities}
            onChange={(e) => handleParkChange(index, 'amenities', e.target.value)}
            placeholder="e.g. Basketball • Trails • Playground"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        
        <div className="md:col-span-2">
          <label className="block text-gray-700 text-sm font-medium mb-1">
            Image URL
          </label>
          <input
            type="url"
            value={park.image}
            onChange={(e) => handleParkChange(index, 'image', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>
    </div>
  ))
)}

<button
  type="button"
  onClick={addPark}
  className="px-4 py-2 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 transition-colors flex items-center"
>
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
  </svg>
  Add Park
</button>
</div>

{/* Transit Section */}
<div className="border border-gray-200 rounded-lg p-6">
  <h2 className="text-xl font-semibold mb-4">Transit Options</h2>
  
  {formData.transit.length === 0 ? (
    <p className="text-gray-500 mb-4">No transit options added yet.</p>
  ) : (
    formData.transit.map((transit, index) => (
      <div key={index} className="p-4 mb-4 border border-gray-200 rounded-lg">
        <div className="flex justify-between mb-2">
          <h3 className="font-medium">Transit Option #{index + 1}</h3>
          <button
            type="button"
            onClick={() => removeTransit(index)}
            className="text-red-500 hover:text-red-700"
          >
            Remove
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
          <div>
            <label className="block text-gray-700 text-sm font-medium mb-1">
              Mode
            </label>
            <select
              value={transit.mode}
              onChange={(e) => handleTransitChange(index, 'mode', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Mode</option>
              <option value="Airport">Airport</option>
              <option value="Bus">Bus</option>
              <option value="Train">Train</option>
              <option value="Subway">Subway</option>
              <option value="Light Rail">Light Rail</option>
              <option value="Ferry">Ferry</option>
            </select>
          </div>
          
          <div>
            <label className="block text-gray-700 text-sm font-medium mb-1">
              Name
            </label>
            <input
              type="text"
              value={transit.name}
              onChange={(e) => handleTransitChange(index, 'name', e.target.value)}
              placeholder="e.g. Harry Reid International"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div className="md:col-span-2">
            <label className="block text-gray-700 text-sm font-medium mb-1">
              Description
            </label>
            <input
              type="text"
              value={transit.description}
              onChange={(e) => handleTransitChange(index, 'description', e.target.value)}
              placeholder="e.g. 15 min drive"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>
    ))
  )}
  
  <button
    type="button"
    onClick={addTransit}
    className="px-4 py-2 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 transition-colors flex items-center"
  >
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
    </svg>
    Add Transit Option
  </button>
</div>

{/* Submit Button */}
<div className="flex justify-center">
<button
            type="button"
            onClick={() => isEditing ? navigate(`/community/${id}`) : navigate('/')}
            className="px-6 py-3 bg-gray-100 text-gray-800 rounded-md hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>

  <button
    type="submit"
    disabled={loading}
    className={`px-8 py-3 rounded-md text-white font-semibold ${
      loading ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
    } transition-colors duration-200 flex items-center`}
  >

{loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {isEditing ? 'Updating...' : 'Submitting...'}
              </>
            ) : (
              isEditing ? 'Update Community' : 'Submit Community'
            )}

  </button>
</div>
</form>
</div>
);
};

export default AddCommunityForm;